// Deterministic terrain generation. Given a seed, every chunk generates
// identically — no global state, so it can run on a worker or a server later.

import { Block } from './blocks';
import { Chunk } from './chunk';
import { CHUNK_SIZE_X, CHUNK_SIZE_Z, SEA_LEVEL, WORLD_HEIGHT } from './constants';
import { mulberry32, ValueNoise2D } from './noise';

const BASE_HEIGHT = 44;
const HEIGHT_VARIATION = 28;

export class WorldGen {
  readonly seed: number;
  private heightNoise: ValueNoise2D;
  private detailNoise: ValueNoise2D;

  constructor(seed: number) {
    this.seed = seed >>> 0;
    this.heightNoise = new ValueNoise2D(this.seed);
    this.detailNoise = new ValueNoise2D((this.seed ^ 0x9e3779b9) >>> 0);
  }

  /** Surface height (top solid block Y) for a world column. */
  heightAt(wx: number, wz: number): number {
    const base = this.heightNoise.fbm(wx / 96, wz / 96, 4);
    const detail = this.detailNoise.fbm(wx / 32, wz / 32, 2) * 0.25;
    const h = BASE_HEIGHT + (base - 0.5 + detail) * HEIGHT_VARIATION;
    return Math.max(1, Math.min(WORLD_HEIGHT - 1, Math.floor(h)));
  }

  /** Deterministic per-column random in [0,1), stable for a given seed. */
  private columnRandom(wx: number, wz: number): number {
    const h = Math.imul(wx | 0, 374761393) ^ Math.imul(wz | 0, 668265263) ^ this.seed;
    return mulberry32(h >>> 0)();
  }

  generateChunk(chunk: Chunk): void {
    for (let lx = 0; lx < CHUNK_SIZE_X; lx++) {
      for (let lz = 0; lz < CHUNK_SIZE_Z; lz++) {
        const wx = chunk.cx * CHUNK_SIZE_X + lx;
        const wz = chunk.cz * CHUNK_SIZE_Z + lz;
        const height = this.heightAt(wx, wz);
        this.fillColumn(chunk, lx, lz, height);
      }
    }
    this.placeTrees(chunk);
    chunk.generated = true;
  }

  private fillColumn(chunk: Chunk, lx: number, lz: number, height: number): void {
    const beach = height <= SEA_LEVEL + 1;
    for (let y = 0; y <= height; y++) {
      let block: Block;
      if (y === 0) {
        block = Block.Bedrock;
      } else if (y === height) {
        block = beach ? Block.Sand : Block.Grass;
      } else if (y >= height - 3) {
        block = beach ? Block.Sand : Block.Dirt;
      } else {
        block = Block.Stone;
      }
      chunk.set(lx, y, lz, block);
    }
    // Fill water above the surface up to sea level.
    for (let y = height + 1; y <= SEA_LEVEL; y++) {
      chunk.set(lx, y, lz, Block.Water);
    }
  }

  private placeTrees(chunk: Chunk): void {
    for (let lx = 2; lx < CHUNK_SIZE_X - 2; lx++) {
      for (let lz = 2; lz < CHUNK_SIZE_Z - 2; lz++) {
        const wx = chunk.cx * CHUNK_SIZE_X + lx;
        const wz = chunk.cz * CHUNK_SIZE_Z + lz;
        const height = this.heightAt(wx, wz);
        if (height <= SEA_LEVEL + 1) continue; // no trees on beaches/water
        if (chunk.get(lx, height, lz) !== Block.Grass) continue;
        if (this.columnRandom(wx, wz) > 0.02) continue; // ~2% of grass columns
        this.growTree(chunk, lx, height + 1, lz);
      }
    }
  }

  private growTree(chunk: Chunk, lx: number, baseY: number, lz: number): void {
    const trunk = 4;
    for (let i = 0; i < trunk; i++) chunk.set(lx, baseY + i, lz, Block.Wood);
    const topY = baseY + trunk;
    // Simple two-layer leaf blob.
    for (let dy = -2; dy <= 0; dy++) {
      const r = dy === 0 ? 1 : 2;
      for (let dx = -r; dx <= r; dx++) {
        for (let dz = -r; dz <= r; dz++) {
          if (dx === 0 && dz === 0 && dy < 0) continue; // keep trunk clear
          const x = lx + dx;
          const z = lz + dz;
          if (x < 0 || x >= CHUNK_SIZE_X || z < 0 || z >= CHUNK_SIZE_Z) continue;
          if (chunk.get(x, topY + dy, z) === Block.Air) {
            chunk.set(x, topY + dy, z, Block.Leaves);
          }
        }
      }
    }
    chunk.set(lx, topY, lz, Block.Leaves);
  }
}
